import { useCallback } from "react";

import { useLocale } from "next-intl";

import { useLocationStore } from "@/store/location.store";
import type { ILocationDataHook } from "@/ui/AddObject/Steps/Preview/types/preview.types";

/**
 * Hook for location data processing following SOLID principles
 * Single Responsibility: handles only location data logic
 * Dependency Inversion: depends on location store abstraction
 */
export const useLocationData = (): ILocationDataHook => {
  const locale = useLocale();
  const { regionValue } = useLocationStore();

  // Single method responsibility: find item by name in any language
  const findByNameInAnyLanguage = useCallback(
    (items: any[], name: string, itemName: string = "name") => {
      return items.find((item) => {
        const propName = item[itemName];

        // Array of all localizations for propName
        return Object.values(propName).includes(name);
      });
    },
    [],
  );

  // Single method responsibility: get localized location name
  const getLocalizedLocationName = useCallback(
    (name: string, locationType: string, targetLocale = locale): string => {
      if (!name) {
        return "";
      }

      if (locationType === "region") {
        const region = findByNameInAnyLanguage(regionValue, name);
        return region ? region.name[targetLocale] || region.name.en : name;
      }

      if (locationType === "city") {
        const citiesNames = regionValue.flatMap((region) => region.cities);

        const city = findByNameInAnyLanguage(citiesNames || [], name);
        return city ? city.name[targetLocale] || city.name.en : name;
      }

      if (locationType === "district") {
        // Find district in nested structure
        for (const region of regionValue) {
          for (const city of region.cities || []) {
            const district = findByNameInAnyLanguage(
              city.districts || [],
              name,
            );
            if (district) {
              return district.name[targetLocale] || district.name.en;
            }
          }
        }
        return name;
      }

      if (locationType === "microdistricts") {
        // Find district in nested structure
        for (const region of regionValue) {
          for (const city of region.cities || []) {
            for (const district of city.districts) {
              const microdistrict = findByNameInAnyLanguage(
                district.microdistricts || [],
                name,
              );
              if (microdistrict) {
                return (
                  microdistrict.name[targetLocale] || microdistrict.name.en
                );
              }
            }
          }
        }
        return name;
      }

      if (locationType === "metrostation") {
        // Find district in nested structure
        for (const region of regionValue) {
          for (const city of region.cities || []) {
            const station = findByNameInAnyLanguage(
              city.metroStations || [],
              name,
            );
            if (station) {
              return station.name[targetLocale] || station.name.en;
            }
          }
        }
        return name;
      }

      if (locationType === "street") {
        // Find street in nested structure
        for (const region of regionValue) {
          for (const city of region.cities || []) {
            for (const district of city.districts || []) {
              const street = findByNameInAnyLanguage(
                district.streets || [],
                name,
              );
              if (street) {
                return street.name[targetLocale] || street.name.en;
              }
            }
          }
        }
        return name;
      }

      return name;
    },
    [locale, regionValue, findByNameInAnyLanguage],
  );

  return {
    getLocalizedLocationName,
    findByNameInAnyLanguage,
  };
};
